import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import {
  Wrench, BarChart2, Package, Search, AlertTriangle,
  LayoutDashboard, ClipboardCheck, RefreshCw, ShieldCheck, ListChecks, TrendingUp,
  Eye, Settings, TrendingDown, Clock, Bell, Users, FileText, Calendar
} from "lucide-react";

// Single canonical ordered list of every Home tile. The user's role controls
// which tiles they see (see Admin Panel → Role Access). Main Admin sees all.
// Order is authoritative — DO NOT re-order without a matching UX request.
const ALL_TILES = [
  { path: "/dashboard",                  label: "Live Dashboard",         sublabel: "लाइव डैशबोर्ड",       icon: LayoutDashboard, color: "#1A3560" },
  { path: "/inspection-queue",           label: "Inspection Queue",       sublabel: "निरीक्षण क़तार",       icon: Bell,            color: "#DC2626" },
  { path: "/production/start",           label: "Start Production Job",   sublabel: "उत्पादन शुरू करें",    icon: BarChart2,       color: "#15803D" },
  { path: "/checksheet",                 label: "Machine Checksheet",     sublabel: "मशीन चेकशीट",          icon: ClipboardCheck,  color: "#1A3560" },
  { path: "/rm-receipt",                 label: "RM Receipt",             sublabel: "कच्चा माल रसीद",       icon: Package,         color: "#1A3560" },
  { path: "/inspection",                 label: "Quality Inspection",     sublabel: "गुणवत्ता निरीक्षण",    icon: Search,          color: "#1A3560" },
  { path: "/missing-data",               label: "Missing Data",           sublabel: "लापता डेटा",           icon: AlertTriangle,   color: "#DC2626" },
  { path: "/visual-inspection",          label: "Visual Inspection",      sublabel: "दृश्य निरीक्षण",       icon: Eye,             color: "#7C3AED" },
  { path: "/admin/shift-summary",        label: "Shift-end Summary",      sublabel: "शिफ्ट सारांश",         icon: Clock,           color: "#15803D" },
  { path: "/admin/setup-ledger",         label: "Setup Stroke Ledger",    sublabel: "सेटअप लॉग",            icon: Settings,        color: "#D97706" },
  { path: "/admin/pm-calendar",          label: "PM Calendar",            sublabel: "पीएम कैलेंडर",         icon: Calendar,        color: "#8B5CF6" },
  { path: "/admin/downtime-report",      label: "Downtime Report",        sublabel: "डाउनटाइम रिपोर्ट",     icon: TrendingDown,    color: "#DC2626" },
  { path: "/loaded-tools",               label: "Loaded Tools",           sublabel: "मशीन पर लगे टूल",      icon: Wrench,          color: "#0E7490" },
  { path: "/admin/tool-history",         label: "Tool History Card",      sublabel: "टूल इतिहास कार्ड",     icon: FileText,        color: "#1A3560" },
  { path: "/inspection-results",         label: "Inspection Results & SPC", sublabel: "परिणाम व SPC",       icon: TrendingUp,      color: "#15803D" },
  { path: "/production/update",          label: "Update Production Qty",  sublabel: "मात्रा अपडेट करें",    icon: RefreshCw,       color: "#D97706" },
  { path: "/rm-issues",                  label: "RM Issue Log",           sublabel: "जारी माल लॉग",         icon: ListChecks,      color: "#D97706" },
  { path: "/admin/operator-performance", label: "Operator Performance",   sublabel: "ऑपरेटर प्रदर्शन",      icon: Users,           color: "#15803D" },
  { path: "/admin",                      label: "Admin Panel",            sublabel: "व्यवस्थापक पैनल",       icon: ShieldCheck,     color: "#1A3560" },
];

const Home = () => {
  const { user, isFeatureEnabled } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "Main Admin";
  // Admin sees everything; every other role obeys the Role-Access matrix.
  const tiles = isAdmin ? ALL_TILES : ALL_TILES.filter((t) => isFeatureEnabled(t.path));

  const now = new Date();
  const timeStr = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  const dateStr = now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
  const minutes = now.getHours() * 60 + now.getMinutes();
  const SHIFT_START = 9 * 60, BREAK_START = 13 * 60, BREAK_END = 13 * 60 + 30, SHIFT_END = 19 * 60 + 30;
  let shift, dotClass;
  if (minutes >= SHIFT_START && minutes < BREAK_START) {
    shift = "Shift 1 (09:00–13:00)"; dotClass = "bg-green-400 animate-pulse";
  } else if (minutes >= BREAK_START && minutes < BREAK_END) {
    shift = "Lunch Break (13:00–13:30)"; dotClass = "bg-yellow-400";
  } else if (minutes >= BREAK_END && minutes < SHIFT_END) {
    shift = "Shift 1 (13:30–19:30)"; dotClass = "bg-green-400 animate-pulse";
  } else {
    shift = "Off Shift (Plant closed)"; dotClass = "bg-slate-400";
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      {/* Welcome banner */}
      <div className="bg-[#1A3560] px-4 pb-6 pt-2">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-white text-xl font-bold">
            Welcome, {user?.full_name?.split(" ")[0]}
          </h2>
          <p className="text-white/60 text-sm mt-0.5">{dateStr} • {timeStr}</p>
          <div className="mt-2 inline-flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5">
            <div className={`w-2 h-2 rounded-full ${dotClass}`}></div>
            <span className="text-white/80 text-xs font-medium">{shift}</span>
          </div>
        </div>
      </div>

      {/* Tiles grid */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">
          {user?.role} — Quick Actions
        </p>
        <div className="grid grid-cols-2 gap-4">
          {tiles.map((tile, i) => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.path + i}
                data-testid={`tile-${tile.path.replace(/\//g, "-").slice(1)}`}
                onClick={() => navigate(tile.path)}
                className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex flex-col items-start gap-3 hover:shadow-md active:scale-[0.97] transition-all text-left"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${tile.color}15` }}
                >
                  <Icon size={24} style={{ color: tile.color }} />
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-sm leading-tight">{tile.label}</p>
                  <p className="text-slate-400 text-xs mt-0.5">{tile.sublabel}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* User info card */}
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-4">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">My Profile</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-slate-400 text-xs">User ID</p>
              <p className="font-bold text-slate-900">{user?.user_id}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Role</p>
              <p className="font-bold text-slate-900">{user?.role}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Department</p>
              <p className="font-bold text-slate-900">{user?.department || "—"}</p>
            </div>
            {user?.machine_assigned && (
              <div>
                <p className="text-slate-400 text-xs">Assigned Machine</p>
                <p className="font-bold text-slate-900">{user.machine_assigned}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
